import { ReactNode, useState, useEffect } from "react";
import { getPrefetchProps } from "@/lib/routePrefetch";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Home,
  User,
  Calendar,
  Star,
  DollarSign,
  Phone,
  Building2,
  Menu,
  X,
  LogOut,
  Shield,
  Briefcase,
  MessageSquare,
  Users,
  Wallet,
  ArrowLeft,
  Eye,
} from "lucide-react";
import { Logo } from "@/components/ui/Logo";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { useUnreadMessages } from "@/hooks/useUnreadMessages";
import { NotificationBell } from "@/components/notifications/NotificationBell";
import { useModelImpersonation } from "@/hooks/useModelImpersonation";

interface AppLayoutProps {
  children: ReactNode;
}

const navItems = [
  { href: "/", label: "HOME", icon: Home },
  { href: "/profile", label: "Meu Perfil", icon: User },
  { href: "/calendario", label: "Calendário", icon: Calendar },
  { href: "/conteudo-exclusivo", label: "Conteúdo Exclusivo", icon: Star },
  { href: "/mensagens", label: "Chat", icon: MessageSquare, isChat: true },
  { href: "/financeiro", label: "Relatório Financeiro", icon: DollarSign },
  { href: "/banco", label: "Banco", icon: Wallet },
  { href: "/afiliado", label: "Afiliado", icon: Users },
  { href: "/contato", label: "Contato", icon: Phone },
  { href: "/sobre", label: "Sobre a Agência", icon: Building2 },
];

export function AppLayout({ children }: AppLayoutProps) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signOut, profile, userRole, isLoading } = useAuth();
  const unreadCount = useUnreadMessages();
  const { isImpersonating, impersonatedProfile } = useModelImpersonation();

  const isAdmin = userRole === "admin";
  const isEmployee = userRole === "employee";

  // Redirect employees and admins to their respective dashboards
  // BUT skip redirect when impersonating a model (viewAs param present)
  useEffect(() => {
    if (isImpersonating) return; // Don't redirect when viewing as model
    if (!isLoading && userRole === "admin") {
      navigate("/admin");
    } else if (!isLoading && userRole === "employee") {
      navigate("/employee");
    }
  }, [userRole, isLoading, navigate, isImpersonating]);

  // Display profile: use impersonated profile data when available
  const displayProfile = isImpersonating && impersonatedProfile
    ? impersonatedProfile
    : profile;

  // Helper to preserve viewAs param in navigation links
  const getHrefWithViewAs = (baseHref: string) => {
    const viewAs = searchParams.get("viewAs");
    if (viewAs) {
      return `${baseHref}?viewAs=${viewAs}`;
    }
    return baseHref;
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/auth");
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Impersonation Banner */}
      {isImpersonating && impersonatedProfile && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-amber-500 text-black px-4 py-2 flex items-center justify-between text-sm font-medium">
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4" />
            <span>Visualizando como: <strong>{impersonatedProfile.german_artistic_name || impersonatedProfile.username}</strong></span>
          </div>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-black hover:bg-amber-600 gap-1"
            onClick={() => navigate("/admin/modelos")}
          >
            <ArrowLeft className="w-3 h-3" />
            Voltar ao Admin
          </Button>
        </div>
      )}

      {/* Mobile Header */}
      <header className={cn(
        "fixed left-0 right-0 z-50 bg-foreground text-background border-b border-border lg:hidden",
        isImpersonating ? "top-10" : "top-0"
      )}>
        <div className="relative flex items-center justify-center px-4 h-16">
          <div className="absolute left-4">
            <NotificationBell variant="dark" />
          </div>
          <div className="flex items-center gap-2">
            <User className="h-5 w-5 text-background/80" />
            <span className="font-display text-lg tracking-wide">MODELO</span>
          </div>
          <div className="absolute right-4">
            <Button
              variant="ghost"
              size="icon"
              className="text-background hover:bg-background/10"
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            >
              {isSidebarOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </Button>
          </div>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {isSidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-foreground/20 backdrop-blur-sm lg:hidden"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar - Dark theme like admin */}
      <aside
        className={cn(
          "fixed top-0 left-0 z-50 h-full w-72 bg-foreground text-background transform transition-transform duration-300 ease-in-out lg:translate-x-0",
          isSidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo with Notification Bell */}
          <div className="p-6 border-b border-background/10 hidden lg:flex items-center justify-between">
            <div className="flex items-center gap-3">
              <User className="h-6 w-6 text-background/80" />
              <span className="font-display text-xl tracking-wide">MODELO PANEL</span>
            </div>
            <NotificationBell variant="dark" />
          </div>

          {/* User Info */}
          <div className="p-4 border-b border-background/10 mt-16 lg:mt-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-background/10 flex items-center justify-center overflow-hidden">
                {displayProfile?.avatar_url ? (
                  <img
                    src={displayProfile.avatar_url}
                    alt={displayProfile?.username}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User className="w-5 h-5 text-background/60" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate text-background">
                  {displayProfile?.username || "Usuário"}
                </p>
                <p className="text-xs text-background/60 truncate">
                  {isImpersonating ? "Visualização Admin" : "Modelo"}
                </p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto p-4 space-y-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.href;
              const Icon = item.icon;
              const isChat = 'isChat' in item && item.isChat;

              return (
                <Link
                  key={item.href}
                  to={getHrefWithViewAs(item.href)}
                  onClick={() => setIsSidebarOpen(false)}
                  {...getPrefetchProps(item.href)}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200",
                    isActive
                      ? "bg-background text-foreground"
                      : "text-background/70 hover:bg-background/10 hover:text-background"
                  )}
                >
                  <div className="relative">
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    {isChat && unreadCount > 0 && (
                      <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1">
                        {unreadCount > 99 ? "99+" : unreadCount}
                      </span>
                    )}
                  </div>
                  <span>{item.label}</span>
                  {isChat && unreadCount > 0 && (
                    <span className="ml-auto bg-red-500 text-white text-xs font-bold rounded-full px-2 py-0.5">
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Admin Link */}
          {isAdmin && (
            <div className="p-4 border-t border-background/10">
              <Link to="/admin">
                <Button
                  variant="ghost"
                  className="w-full justify-start text-background/70 hover:text-background hover:bg-background/10"
                >
                  <Shield className="w-5 h-5 mr-3" />
                  Painel Admin
                </Button>
              </Link>
            </div>
          )}

          {/* Employee Link */}
          {isEmployee && (
            <div className="p-4 border-t border-background/10">
              <Link to="/employee">
                <Button
                  variant="ghost"
                  className="w-full justify-start text-background/70 hover:text-background hover:bg-background/10"
                >
                  <Briefcase className="w-5 h-5 mr-3" />
                  Painel Funcionário
                </Button>
              </Link>
            </div>
          )}

          {/* Logout */}
          <div className="p-4 border-t border-background/10">
            <Button
              variant="ghost"
              className="w-full justify-start text-background/50 hover:text-red-400 hover:bg-background/5"
              onClick={handleSignOut}
            >
              <LogOut className="w-5 h-5 mr-3" />
              Sair
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className={cn(
        "lg:pl-72 min-h-screen",
        isImpersonating ? "pt-26 lg:pt-10" : "pt-16 lg:pt-0"
      )}>
        <div className="p-4 lg:p-8">{children}</div>
      </main>
    </div>
  );
}
